import React, { Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { HMSRoomProvider, selectIsConnectedToRoom, useHMSActions, useHMSStore } from '@100mslive/react-sdk';
import { Box, globalStyles, HMSThemeProvider } from '@100mslive/roomkit-react';
import { AppData } from './components/AppData/AppData.jsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import FullPageProgress from './components/FullPageProgress';
import { Init } from './components/init/Init';
import { KeyboardHandler } from './components/Input/KeyboardInputManager';
import { Notifications } from './components/Notifications';
import { ScreenShareProvider } from './components/Preview/PreviewJoin/components/ScreenshareContext.jsx';
import { ToastContainer } from './components/Toast/ToastContainer';
import store from './redux/store';
import { palette } from './theme.js';
import { Confetti } from './plugins/confetti';
import { RemoteStopScreenshare } from './plugins/RemoteStopScreenshare';
import { shadeColor } from './common/utils';
import { FeatureFlags } from './services/FeatureFlags';
import SentryRoomTagger from './services/SentryRoomTagger.jsx';
import { generateUrl } from './utils/helper.jsx';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './assets/fonts/stylesheet.css';
import './assets/css/portal.css';

// Route-level code splitting: each route's JS and CSS load as their own chunk instead of
// being bundled into one monolithic main.css that blocks first paint on every route.
// lazyWithRetry reloads once when a chunk from a previous deploy is gone (see utils/lazyWithRetry).
const RecordingRoom = lazyWithRetry(() => import('./components/recordingRoom'));
const FeedbackRoom = lazyWithRetry(() => import('./components/feedbackRoom'));
const WeeklyReport = lazyWithRetry(() => import('./components/WeeklyReport/index.jsx'));
const PracticeRoom = lazyWithRetry(() => import('./components/PracticeRoom'));
const PreviewScreen = lazyWithRetry(() => import('./components/PreviewScreen'));
const InterviewPlatformView = lazyWithRetry(() => import('./components/InterviewPlatformView.jsx'));
const CodingAndAptitude = lazyWithRetry(() => import('./components/CodingAndAptitude.jsx'));
const InterviewStart = lazyWithRetry(() => import('./components/Pages/components/PreviewDesktop/InterviewStart.jsx'));
const DeviceCheck = lazyWithRetry(() => import('./components/DeviceCheck/DeviceCheck.jsx'));
const PostLeave = lazyWithRetry(() => import('./components/PostLeave'));
const InterviewSignUp = lazyWithRetry(() => import('./components/Pages/components/PreviewDesktop/InterviewSignUp.jsx'));
const CefrViewConnectorNew = lazyWithRetry(() => import('./layouts/CefrViewConnectorNew.jsx'));
const Other = lazyWithRetry(() => import('./components/Pages/Other.jsx'));

const defaultTokenEndpoint = generateUrl('token_generation');
const envPolicyConfig = JSON.parse('{}');

let appName;
if (window.location.host.includes('localhost')) {
    appName = 'localhost';
} else {
    appName = window.location.host.split('.')[0];
}

document.title = 'InCruiter - One Way Interview Platform' || `${appName}'s ${document.title}`;

// TODO: remove now that there are options to change to portrait
const getAspectRatio = ({ width, height }) => {
    const host = process.env.REACT_APP_HOST_NAME || window.location.hostname;
    const portraitDomains = (process.env.REACT_APP_PORTRAIT_MODE_DOMAINS || '').split(',');
    if (portraitDomains.includes(host) && width > height) {
        return { width: height, height: width };
    }
    return { width, height };
};

export function EdtechComponent({
    tokenEndpoint = defaultTokenEndpoint,
    themeConfig: {
        aspectRatio = '1-1',
        color = '#2F80FF',
        theme = 'dark',
        logo = '',
        headerPresent = 'false',
        metadata = '',
        recordingUrl = `${generateUrl('meeting')}/preview/beam`,
    },
    policyConfig = envPolicyConfig,
    getDetails = () => {},
    authTokenByRoomCodeEndpoint = '',
}) {
    const { 0: width, 1: height } = aspectRatio.split('-').map((el) => parseInt(el));
    globalStyles();
    return (
        <Provider store={store}>
            <ErrorBoundary>
                <HMSThemeProvider
                    themeType={theme}
                    aspectRatio={getAspectRatio({ width, height })}
                    theme={{
                        colors: {
                            ...palette[theme],
                            primary_default: color,
                            primary_dim: shadeColor(color, -30),
                            primary_bright: shadeColor(color, 30),
                            primary_disabled: shadeColor(color, 10),
                        },
                    }}
                >
                    <HMSRoomProvider isHMSStatsOn={FeatureFlags.enableStatsForNerds} leaveOnUnload={false}>
                        <AppData
                            appDetails={metadata}
                            policyConfig={policyConfig}
                            logo={logo}
                            recordingUrl={recordingUrl}
                            tokenEndpoint={tokenEndpoint}
                        />
                        <Init />
                        <Box
                            css={{
                                bg: '#191924',
                                w: '100%',
                                lineHeight: '1.5',
                                '-webkit-text-size-adjust': '100%',
                                ...(headerPresent === 'true' ? { flex: '1 1 0', minHeight: 0 } : { h: '100%' }),
                            }}
                        >
                            <AppRoutes
                                getDetails={getDetails}
                                authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint}
                            />
                        </Box>
                    </HMSRoomProvider>
                </HMSThemeProvider>
            </ErrorBoundary>
        </Provider>
    );
}

const cefrQuestions = [
    {
        questionId: 1,
        questionText: 'What time does the class start?',
        options: ['9:00', '9:15', '9:30', '10:00'],
        correctAnswer: '9:30',
        audioScript:
            'Hi Riya, did you hear about the class tomorrow?\nYes, I did. It starts at 9 in the morning, right?\nAlmost. It actually starts at 9:30, so don’t be late.\nOkay, good to know. Where is it happening?\nIn Room 204, near the library. The teacher said we should bring our notebooks.\nDo we need to bring our laptops too?\nNo, laptops are not needed. It’s just a short class about the test next week.\nAlright. How long will the class be?\nOnly for one hour, so it will finish by 10:30.',
        audioUrl: 'https://incruiter.blob.core.windows.net/dev/AzureAiAudioFile/audio-1772088127.437-134329.mp3',
        timeToAnswer: 360,
        section: 'Listening',
    },
    {
        questionId: 2,
        questionText: 'Where will the class take place?',
        options: ['Main hall', 'Room 204', 'Room 104', 'Computer lab'],
        correctAnswer: 'Room 204',
        audioScript:
            'Hi Riya, did you hear about the class tomorrow?\nYes, I did. It starts at 9 in the morning, right?\nAlmost. It actually starts at 9:30, so don’t be late.\nOkay, good to know. Where is it happening?\nIn Room 204, near the library. The teacher said we should bring our notebooks.\nDo we need to bring our laptops too?\nNo, laptops are not needed. It’s just a short class about the test next week.\nAlright. How long will the class be?\nOnly for one hour, so it will finish by 10:30.',
        audioUrl: 'https://incruiter.blob.core.windows.net/dev/AzureAiAudioFile/audio-1772088127.437-134329.mp3',
        timeToAnswer: 0,
        section: 'Listening',
    },
    {
        questionId: 5,
        question: 'The meaning of ‘determined’ is .......',
        options: ['mind made up', 'mind turn up', 'mind fade up', 'mind turn down'],
        correctAnswerIndex: 0,
        marks: 1,
        passage:
            'About the year 1900, a small, dark-haired boy named Charles Chaplin was often seen waiting outside the back entrances of London theatres. He looked thin and hungry but his blue eyes were determined. He was hoping to get work in show business. He could sing and dance. His parents were music-hall performers and he had been born into the life of the theatre. And, although his own boyhood was painfully hard, he knew how to make people laugh. His own father had died from drinking too much. And his mother was not really able to look after Charles and his older half-brother, Sid. She was often sick in mind and had to be sent to hospital.',
        timeToAnswer: 480,
        section: 'Reading',
    },
    {
        _id: {
            $oid: '699d47c02cc4f22c9e390498',
        },
        section: 'Writing',
        level: 'easy',
        cefr_range: ['A2', 'B1'],
        recommended_time_minutes: 5,
        task_id: 'E_WRT_002',
        task_type: 'short_paragraph',
        skill: 'daily_life',
        prompt: 'Write a short paragraph about your daily routine. Describe what you do in the morning and evening.',
        min_word_count: 50,
        max_word_count: 80,
        guidelines: [
            'Use simple present tense',
            'Mention at least two daily activities',
            'Write clear and short sentences',
        ],
        timeToAnswer: 600,
    },
    {
        _id: {
            $oid: '699d47c02cc4f22c9e390499',
        },
        section: 'Writing',
        level: 'easy',
        cefr_range: ['A2', 'B1'],
        recommended_time_minutes: 5,
        task_id: 'E_WRT_003',
        task_type: 'short_paragraph',
        skill: 'personal_preferences',
        prompt: 'Write a short paragraph about your favorite food or drink. Explain why you like it.',
        min_word_count: 50,
        max_word_count: 80,
        guidelines: ['Use simple sentences', 'Give at least one reason', 'Focus on clarity'],
        timeToAnswer: 0,
    },
    {
        _id: {
            $oid: '699d47062cc4f22c9e390485',
        },
        section: 'Speaking',
        level: 'easy',
        cefr_range: ['A2', 'B1'],
        recommended_time_minutes: 5,
        task_id: 'E_SPK_001',
        task_type: 'guided_speech',
        skill: 'personal_information',
        task: 'Talk about yourself. Describe your daily routine and one hobby you enjoy.',
        min_duration_seconds: 45,
        max_duration_seconds: 60,
        guidelines: ['Speak clearly and slowly', 'Use simple sentences', 'Mention at least two daily activities'],
        timeToAnswer: 600,
    },
    {
        _id: {
            $oid: '699d47062cc4f22c9e390486',
        },
        section: 'Speaking',
        level: 'easy',
        cefr_range: ['A2', 'B1'],
        recommended_time_minutes: 5,
        task_id: 'E_SPK_002',
        task_type: 'guided_speech',
        skill: 'daily_life',
        task: 'Talk about your morning routine. Explain what you do after waking up.',
        min_duration_seconds: 45,
        max_duration_seconds: 60,
        guidelines: ['Speak clearly and slowly', 'Use simple present tense', 'Mention at least two activities'],
        timeToAnswer: 0,
    },
    {
        _id: {
            $oid: '699d47062cc4f22c9e390487',
        },
        section: 'Speaking',
        level: 'easy',
        cefr_range: ['A2', 'B1'],
        recommended_time_minutes: 5,
        task_id: 'E_SPK_003',
        task_type: 'guided_speech',
        skill: 'free_time',
        task: 'Describe one activity you like to do in your free time. Explain when and why you enjoy it.',
        min_duration_seconds: 45,
        max_duration_seconds: 60,
        guidelines: ['Use simple words', 'Give one reason', 'Speak in complete sentences'],
        timeToAnswer: 0,
    },
    {
        _id: {
            $oid: '699d47062cc4f22c9e390488',
        },
        section: 'Speaking',
        level: 'easy',
        cefr_range: ['A2', 'B1'],
        recommended_time_minutes: 5,
        task_id: 'E_SPK_004',
        task_type: 'guided_speech',
        skill: 'family_and_friends',
        task: 'Talk about a family member or a close friend. Describe who they are and what they do.',
        min_duration_seconds: 45,
        max_duration_seconds: 60,
        guidelines: ['Use basic descriptive words', 'Mention their name or role', 'Speak slowly and clearly'],
        timeToAnswer: 0,
    },
    {
        _id: {
            $oid: '699d47062cc4f22c9e390489',
        },
        section: 'Speaking',
        level: 'easy',
        cefr_range: ['A2', 'B1'],
        recommended_time_minutes: 5,
        task_id: 'E_SPK_005',
        task_type: 'guided_speech',
        skill: 'places',
        task: 'Describe a place you like. Explain where it is and why you like it.',
        min_duration_seconds: 45,
        max_duration_seconds: 60,
        guidelines: ['Use simple location words', 'Give one reason', 'Use short sentences'],
        timeToAnswer: 0,
    },
];

const RouteList = ({ authTokenByRoomCodeEndpoint }) => {
    return (
        <Routes>
            <Route
                path="recording/room/:interviewId/:roomId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <RecordingRoom />
                    </Suspense>
                }
            />
            <Route
                path="feedback/room/:interviewId/:roomId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <FeedbackRoom />
                    </Suspense>
                }
            />
            <Route
                path="conversion-report/:userId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <WeeklyReport />
                    </Suspense>
                }
            />
            <Route
                path="practice/room/:role/:roomId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <PracticeRoom />
                    </Suspense>
                }
            />
            <Route
                path="preview/:role/:roomId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <SentryRoomTagger />
                        <PreviewScreen authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint} />
                    </Suspense>
                }
            />
            <Route
                path="meeting/:role/:roomId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <SentryRoomTagger />
                        <InterviewPlatformView />
                    </Suspense>
                }
            />
            <Route
                path="ca-meeting/:role/:roomId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <SentryRoomTagger />
                        <CodingAndAptitude />
                    </Suspense>
                }
            />
            <Route path="interview-room">
                <Route
                    path=":role/:roomId"
                    element={
                        <Suspense fallback={<FullPageProgress />}>
                            <SentryRoomTagger />
                            <InterviewStart />
                        </Suspense>
                    }
                />
                <Route
                    path=":role/:roomId/send"
                    element={
                        <Suspense fallback={<FullPageProgress />}>
                            <SentryRoomTagger />
                            <InterviewStart />
                        </Suspense>
                    }
                />
            </Route>
            <Route
                path="device-check"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <DeviceCheck />
                    </Suspense>
                }
            />
            <Route
                path="leave/:role/:roomId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <PostLeave />
                    </Suspense>
                }
            />
            <Route
                path="interview/:positionId/:questionId/:linkId"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <InterviewSignUp />
                    </Suspense>
                }
            />
            <Route
                path="/cefr"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <CefrViewConnectorNew cefrQuestions={cefrQuestions} />
                    </Suspense>
                }
            />
            <Route
                path="*"
                element={
                    <Suspense fallback={<FullPageProgress />}>
                        <Other page="fofPage" />
                    </Suspense>
                }
            />
        </Routes>
    );
};

const BackSwipe = () => {
    const isConnectedToRoom = useHMSStore(selectIsConnectedToRoom);
    const hmsActions = useHMSActions();
    useEffect(() => {
        const onRouteLeave = async () => {
            if (isConnectedToRoom) {
                try {
                    await hmsActions.leave();
                } catch (err) {
                    console.error('Error leaving HMS room', err);
                }
            }
        };
        window.addEventListener('popstate', onRouteLeave);
        return () => {
            window.removeEventListener('popstate', onRouteLeave);
        };
    }, [hmsActions, isConnectedToRoom]);
    return null;
};

function AppRoutes({ getDetails, authTokenByRoomCodeEndpoint }) {
    return (
        <ScreenShareProvider>
            <Router>
                <ToastContainer />
                <Notifications />
                <BackSwipe />
                <Confetti />
                <RemoteStopScreenshare />
                <KeyboardHandler />
                <Routes>
                    <Route
                        path="/*"
                        element={<RouteList authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint} />}
                    />
                </Routes>
            </Router>
        </ScreenShareProvider>
    );
}

export default function App() {
    return (
        <EdtechComponent
            themeConfig={{
                aspectRatio: '1-1',
                theme: 'dark',
                color: '#2F80FF',
                logo: '',
                headerPresent: process.env.REACT_APP_HEADER_PRESENT,
                metadata: '{}', // A stringified object in env
            }}
        />
    );
}
